import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FaCalendarAlt } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "../Create.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  clearSectionOptions,
  fetchClassOptions,
  fetchSectionOptions,
} from "../../../../redux/Masterdata";
import {
  CustomButton,
  CustomCheckbox,
  CustomDatePicker,
  CustomDropdown,
  CustomTextInput,
  CustomTimePicker,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const ExamScheduleCreate = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const scheduleData = location.state?.scheduleData || null;
  const editId = scheduleData?.id || null;
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const client_id = clientData?.client_id || "";
  const user_id = user?.user_id || user?.id || "";

  const { classOptions, sectionOptions } = useSelector(
    (state) => state.masterData,
  );

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [examNameOptions, setExamNameOptions] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // Exam status coming from the schedule API.
  // 1 = Active  -> normal flow with full validation.
  // 0 = Inactive -> inputs disabled, no validation, direct submit.
  const [examStatus, setExamStatus] = useState(1);

  const [formData, setFormData] = useState({
    examName: "",
    classId: "",
    sectionId: "",
    startDate: "",
    endDate: "",
  });
  const [timeValidationError, setTimeValidationError] = useState({
    open: false,
    subjectName: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // Derived flag: when status is 0 the subject inputs are disabled.
  const inputsDisabled = examStatus === 0;

  // Format time for API submission (HH:mm:ss)
  const formatTimeForAPI = (timeValue) => {
    if (!timeValue) return "";
    try {
      if (dayjs.isDayjs(timeValue)) {
        return timeValue.format("HH:mm:ss");
      }
      if (
        typeof timeValue === "string" &&
        timeValue.match(/^\d{1,2}:\d{2}(:\d{2})?$/)
      ) {
        const parts = timeValue.split(":");
        const hours = parts[0].padStart(2, "0");
        const minutes = parts[1].padStart(2, "0");
        const seconds = parts[2] ? parts[2].padStart(2, "0") : "00";
        return `${hours}:${minutes}:${seconds}`;
      }
      if (typeof timeValue === "string") {
        const parsed = dayjs(timeValue);
        if (parsed.isValid()) {
          return parsed.format("HH:mm:ss");
        }
      }
      return "";
    } catch (error) {
      console.error("Error formatting time for API:", timeValue, error);
      return "";
    }
  };

  // Fetch exam name options
  const fetchExamNames = useCallback(async () => {
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };
      const response = await AxiosInstance.post(
        "/exam/examConfig/list",
        payload,
      );
      if (response?.data?.status) {
        const examData = response.data.data || [];
        const options = examData.map((item) => {
          const baseLabel = item.exam_type || item.exam_name;
          const statusText = item.isActive === 1 ? "Active" : "Inactive";
          return {
            value: item.id,
            label: `${baseLabel} (${statusText})`,
          };
        });
        setExamNameOptions(options);
      }
    } catch (error) {
      console.error("Error fetching exam names:", error);
    }
  }, [school_id, academic_year_id]);

  // Load initial data
  useEffect(() => {
    fetchExamNames();
    dispatch(
      fetchClassOptions({
        id: parseInt(school_id),
        academic_year: academic_year,
      }),
    );
  }, [dispatch, school_id, academic_year, fetchExamNames]);

  // Fetch sections when class changes
  useEffect(() => {
    if (formData.classId) {
      const classValue = formData.classId?.value || formData.classId;
      dispatch(
        fetchSectionOptions({
          id: [parseInt(classValue)],
          school_id: parseInt(school_id),
        }),
      );
    } else {
      dispatch(clearSectionOptions());
    }
  }, [dispatch, formData.classId, school_id]);

  // Auto-fetch subjects when all 3 dropdowns are selected (only in create mode)
  useEffect(() => {
    const examValue = formData.examName?.value || formData.examName;
    const classValue = formData.classId?.value || formData.classId;
    const sectionValue = formData.sectionId?.value || formData.sectionId;

    if (examValue && classValue && sectionValue && !editId) {
      fetchSubjects(examValue, classValue, sectionValue);
    }
  }, [formData.examName, formData.classId, formData.sectionId]);

  // Load edit data using GET API
  useEffect(() => {
    if (editId) {
      fetchEditData();
    }
  }, [editId]);

  const fetchSubjects = async (examId, classId, sectionId) => {
    try {
      setLoading(true);
      const payload = {
        academic_id: parseInt(academic_year_id),
        config_id: parseInt(examId),
        class_id: parseInt(classId),
        section_id: sectionId,
      };
      const response = await AxiosInstance.post(
        "/exam/examSchedule/Createlist",
        payload,
      );
      if (response?.data?.status) {
        const data = response.data.data;
        // Capture exam status (0 = inactive -> disable inputs, no validation).
        // Default to 1 (active) when not present.
        setExamStatus(data[0]?.status ?? 1);

        setFormData((prev) => ({
          ...prev,
          startDate: data[0]?.start_date || "",
          endDate: data[0]?.end_date || "",
        }));

        const subjectList = data.flatMap((item) =>
          item.subjects.flatMap((subject) => {
            const output = [];
            if (subject.theory === 1) {
              output.push({
                id: subject.id || 0,
                subject_id: subject.subject_id || "",
                subject_name: subject.subject_name,
                exam_mode: "Theory",
                mode_of_exam: "theory",
                include: true,
                date_of_exam: "",
                pass_mark: "",
                max_mark: "",
                from_time: "",
                to_time: "",
                duration: "",
                markError: "",
              });
            }
            if (subject.practical === 1) {
              output.push({
                id: subject.id || 0,
                subject_id: subject.subject_id || "",
                subject_name: subject.subject_name,
                exam_mode: "Practical",
                mode_of_exam: "practical",
                include: true,
                date_of_exam: "",
                pass_mark: "",
                max_mark: "",
                from_time: "",
                to_time: "",
                duration: "",
                markError: "",
              });
            }
            return output;
          }),
        );
        setSubjects(subjectList);
      } else {
        setSubjects([]);
        setExamStatus(1);
        setFormData((prev) => ({
          ...prev,
          startDate: "",
          endDate: "",
        }));
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
      setExamStatus(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchEditData = async () => {
    try {
      setLoading(true);
      const payload = {
        config_id: scheduleData.config_id,
        class_id: scheduleData.class_id,
        section_id: "",
      };

      const response = await AxiosInstance.post(
        "/exam/examSchedule/view",
        payload,
      );
      if (response?.data?.status) {
        const dataArray = response.data.data || [];
        const data = dataArray[0] || null;

        if (data) {
          // Respect status in edit mode too, if the API returns it.
          if (data.isActive !== undefined && data.isActive !== null) {
            setExamStatus(data.isActive);
          }

          setFormData({
            examName: data.config_id
              ? { value: data.config_id, label: "" }
              : "",
            classId: data.class_id
              ? { value: data.class_id, label: data.class_name || "" }
              : "",
            sectionId: data.section_id
              ? { value: data.section_id, label: data.section_id }
              : "",
            startDate: data.start_date || "",
            endDate: data.end_date || "",
          });

          if (data.class_id) {
            dispatch(
              fetchSectionOptions({
                id: [parseInt(data.class_id)],
                school_id: parseInt(school_id),
              }),
            );
          }

          if (data.schedule_details && data.schedule_details.length > 0) {
            setSubjects(
              data.schedule_details.map((sub) => ({
                id: sub.id || 0,
                subject_id: sub.subject_id || "",
                subject_name: sub.subject_name || "-",
                exam_mode:
                  sub.mode_of_exam === "theory"
                    ? "Theory"
                    : sub.mode_of_exam === "practical"
                      ? "Practical"
                      : sub.mode_of_exam || "Theory",
                mode_of_exam: sub.mode_of_exam || "theory",
                include:
                  sub.subject_include === 1 || sub.subject_include === true,
                date_of_exam: sub.date || "",
                // FIX: use explicit null/undefined checks instead of `||`.
                // `0 || "" === ""` would silently turn a valid 0 into an empty
                // string, which (a) hides the value in the input and (b) makes
                // the "Pass Mark cannot be 0" validation skip it entirely.
                // Keeping it as "0" lets the active-flow validation fire.
                pass_mark:
                  sub.pass_mark !== null && sub.pass_mark !== undefined
                    ? String(sub.pass_mark)
                    : "",
                max_mark:
                  sub.max_marks !== null && sub.max_marks !== undefined
                    ? String(sub.max_marks)
                    : "",
                from_time: sub.from_time
                  ? dayjs(`2000-01-01T${sub.from_time}`)
                  : "",
                to_time: sub.end_time
                  ? dayjs(`2000-01-01T${sub.end_time}`)
                  : "",
                duration:
                  sub.duration !== null && sub.duration !== undefined
                    ? String(sub.duration)
                    : "",
                markError: "",
              })),
            );
          }

          if (data.config_id && examNameOptions.length > 0) {
            const matchedExam = examNameOptions.find(
              (opt) => opt.value === data.config_id,
            );
            if (matchedExam) {
              setFormData((prev) => ({
                ...prev,
                examName: matchedExam,
              }));
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching edit data:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.fetchFailed ||
          "Failed to load exam schedule data",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editId && examNameOptions.length > 0 && formData.examName) {
      const configId = formData.examName?.value || formData.examName;
      const matchedExam = examNameOptions.find((opt) => opt.value === configId);
      if (matchedExam) {
        setFormData((prev) => ({
          ...prev,
          examName: matchedExam,
        }));
      }
    }
  }, [examNameOptions]);

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "classId") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        sectionId: "",
        startDate: "",
        endDate: "",
      }));
      setSubjects([]);
      setExamStatus(1);
    }
    if (name === "sectionId") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        startDate: "",
        endDate: "",
      }));
      setSubjects([]);
      setExamStatus(1);
    }
    if (name === "examName") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        startDate: "",
        endDate: "",
      }));
      setSubjects([]);
      setExamStatus(1);
    }
  };

  const handleSubjectChange = (index, field, value) => {
    setSubjects((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // Auto-calculate duration when from_time or to_time changes
      if (field === "from_time" || field === "to_time") {
        const fromTime =
          field === "from_time" ? value : updated[index].from_time;
        const toTime = field === "to_time" ? value : updated[index].to_time;

        if (fromTime && toTime) {
          const from = dayjs.isDayjs(fromTime) ? fromTime : dayjs(fromTime);
          const to = dayjs.isDayjs(toTime) ? toTime : dayjs(toTime);

          if (from.isValid() && to.isValid()) {
            let diffMinutes = to.diff(from, "minute");
            if (diffMinutes < 0) diffMinutes += 1440;
            const hours = (diffMinutes / 60).toFixed(1);
            updated[index].duration = hours;
          }
        } else {
          updated[index].duration = "";
        }
      }

      // Validate pass_mark and max_mark: only numbers, max 3 digits
      if (field === "pass_mark" || field === "max_mark") {
        const numericValue = value.replace(/[^0-9]/g, "").slice(0, 3);
        updated[index][field] = numericValue;

        const passMark =
          parseInt(
            field === "pass_mark" ? numericValue : updated[index].pass_mark,
          ) || 0;
        const maxMark =
          parseInt(
            field === "max_mark" ? numericValue : updated[index].max_mark,
          ) || 0;

        if (maxMark > 0 && passMark > maxMark) {
          updated[index].markError = `Pass mark cannot exceed maximum mark`;
        } else {
          updated[index].markError = "";
        }
      }

      return updated;
    });
  };

  // Validate on blur for Pass Mark and Maximum Mark fields
  const handleMarkBlur = (index, field) => {
    // Skip validation when status is inactive
    if (inputsDisabled) return;

    const subject = subjects[index];
    const passMark = parseInt(subject.pass_mark) || 0;
    const maxMark = parseInt(subject.max_mark) || 0;
    const subjectName = subject.subject_name;

    // Check if Pass Mark is 0
    if (field === "pass_mark" && subject.pass_mark !== "" && passMark === 0) {
      setModalState({
        open: true,
        type: "error",
        message: `For subject "${subjectName}", Pass Mark cannot be 0`,
        redirectPath: "",
      });
      return;
    }

    // Check if Maximum Mark is 0
    if (field === "max_mark" && subject.max_mark !== "" && maxMark === 0) {
      setModalState({
        open: true,
        type: "error",
        message: `For subject "${subjectName}", Maximum Mark cannot be 0`,
        redirectPath: "",
      });
      return;
    }

    // Check Pass Mark > Maximum Mark (only when both are filled and > 0)
    if (
      subject.pass_mark !== "" &&
      subject.max_mark !== "" &&
      maxMark > 0 &&
      passMark > maxMark
    ) {
      setModalState({
        open: true,
        type: "error",
        message: `For subject "${subjectName}", Pass Mark (${passMark}) cannot exceed Maximum Mark (${maxMark})`,
        redirectPath: "",
      });
    }
  };

  const handleIncludeToggle = (index) => {
    // Do nothing when inputs are disabled
    if (inputsDisabled) return;

    setSubjects((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], include: !updated[index].include };
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const includedSubjects = subjects.filter((sub) => sub.include);
    // Run validation ONLY when status is Active (1).
    // When status is 0 (Inactive) -> skip all validation and submit directly.
    if (!inputsDisabled) {
      const newErrors = {};
      if (!formData.examName)
        newErrors.examName =
          translations.labels?.examNameRequired || "Exam Name is required";
      if (!formData.classId)
        newErrors.classId =
          translations.labels?.classRequired || "Class is required";
      if (!formData.sectionId)
        newErrors.sectionId =
          translations.labels?.sectionRequired || "Section is required";

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      if (includedSubjects.length === 0) {
        setModalState({
          open: true,
          type: "error",
          message:
            translations.labels?.includeAtLeastOneSubject ||
            "Please include at least one subject",
          redirectPath: "",
        });
        return;
      }

      // Validate Pass Mark = 0
      const zeroPassMark = includedSubjects.find(
        (sub) => sub.pass_mark !== "" && parseInt(sub.pass_mark) === 0,
      );
      if (zeroPassMark) {
        setModalState({
          open: true,
          type: "error",
          message: `For subject "${zeroPassMark.subject_name}", Pass Mark cannot be 0`,
          redirectPath: "",
        });
        return;
      }

      // Validate Maximum Mark = 0
      const zeroMaxMark = includedSubjects.find(
        (sub) => sub.max_mark !== "" && parseInt(sub.max_mark) === 0,
      );
      if (zeroMaxMark) {
        setModalState({
          open: true,
          type: "error",
          message: `For subject "${zeroMaxMark.subject_name}", Maximum Mark cannot be 0`,
          redirectPath: "",
        });
        return;
      }

      // Validate that Pass Mark / Maximum Mark are actually filled in.
      // (After the load fix, a value of 0 is preserved as "0", so a truly
      // empty field is the only thing caught here.)
      const emptyMark = includedSubjects.find(
        (sub) => sub.pass_mark === "" || sub.max_mark === "",
      );
      if (emptyMark) {
        setModalState({
          open: true,
          type: "error",
          message: `For subject "${emptyMark.subject_name}", please enter Pass Mark and Maximum Mark`,
          redirectPath: "",
        });
        return;
      }

      // Validate Pass Mark > Maximum Mark
      const markError = includedSubjects.find(
        (sub) =>
          sub.pass_mark !== "" &&
          sub.max_mark !== "" &&
          parseInt(sub.pass_mark) > parseInt(sub.max_mark),
      );
      if (markError) {
        setModalState({
          open: true,
          type: "error",
          message: `For subject "${markError.subject_name}", Pass Mark (${markError.pass_mark}) cannot exceed Maximum Mark (${markError.max_mark})`,
          redirectPath: "",
        });
        return;
      }

      // Validation for required fields and time order
      let hasError = false;
      let timeOrderError = false;
      let errorSubjectName = "";

      for (const sub of includedSubjects) {
        if (!sub.date_of_exam || !sub.from_time || !sub.to_time) {
          hasError = true;
          errorSubjectName = sub.subject_name;
          break;
        }

        if (sub.from_time && sub.to_time) {
          const from = dayjs(sub.from_time);
          const to = dayjs(sub.to_time);

          if (to.isBefore(from)) {
            timeOrderError = true;
            errorSubjectName = sub.subject_name;
            break;
          }
        }
      }

      if (hasError) {
        setModalState({
          open: true,
          type: "error",
          message: `For subject "${errorSubjectName}", please fill Date of Exam, From Time, and To Time`,
          redirectPath: "",
        });
        return;
      }

      if (timeOrderError) {
        setModalState({
          open: true,
          type: "error",
          message: `For subject "${errorSubjectName}", To Time should not be before From Time`,
          redirectPath: "",
        });
        return;
      }
    }

    try {
      setSubmitting(true);

      const examValue = formData.examName?.value || formData.examName;
      const classValue = formData.classId?.value || formData.classId;
      const sectionValue = formData.sectionId?.value || formData.sectionId;

      const schedule_details = includedSubjects.map((sub) => {
        // When the exam status is Inactive (0), the date/time fields
        // are locked and not entered yet, so send null to the API.
        // When Active (1), send the actual entered values.
        const dateValue = inputsDisabled
          ? null
          : sub.date_of_exam
            ? dayjs(sub.date_of_exam).format("YYYY-MM-DD")
            : null;

        const fromTimeValue = inputsDisabled
          ? null
          : formatTimeForAPI(sub.from_time) || null;

        const endTimeValue = inputsDisabled
          ? null
          : formatTimeForAPI(sub.to_time) || null;

        return {
          id: sub.id || 0,
          subject_id: parseInt(sub.subject_id),
          subject_name: sub.subject_name,
          mode_of_exam: sub.mode_of_exam || sub.exam_mode?.toLowerCase(),
          subject_include: sub.include ? 1 : 0,
          date: dateValue,
          from_time: fromTimeValue,
          end_time: endTimeValue,
          duration: sub.duration ? parseFloat(sub.duration).toString() : "0",
          // Use Number() so an empty string becomes NaN guarded by the check,
          // while a legitimate "0" is preserved (won't be the case after
          // validation, but kept robust here).
          pass_mark: sub.pass_mark !== "" ? parseInt(sub.pass_mark) : 0,
          max_marks: sub.max_mark !== "" ? parseInt(sub.max_mark) : 0,
        };
      });

      const payload = {
        client_id: parseInt(client_id),
        school_id: parseInt(school_id),
        academic_id: parseInt(academic_year_id),
        config_id: parseInt(examValue),
        class_id: parseInt(classValue),
        section_id: sectionValue,
        schedule_details: schedule_details,
        isActive: examStatus,
      };

      if (editId) {
        payload.id = parseInt(editId);
        payload.updated_by = parseInt(user_id);
      } else {
        payload.created_by = parseInt(user_id);
      }

      const url = editId
        ? "/exam/examSchedule/update"
        : "/exam/examSchedule/create";

      const response = editId
        ? await AxiosInstance.put(url, payload)
        : await AxiosInstance.post(url, payload);

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: editId
            ? translations.labels?.updateSuccess ||
              "Exam schedule updated successfully"
            : translations.labels?.createSuccess ||
              "Exam schedule created successfully",
          redirectPath: "/user/examSchedule",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            translations.labels?.saveFailed ||
            "Operation failed",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.saveFailed || "Error saving exam schedule",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/examSchedule");
  };

  if (loading && editId) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          {translations.labels?.loading || "Loading..."}
        </div>
      </div>
    );
  }

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {editId
              ? translations.labels?.editExamSchedule || "Edit Exam Schedule"
              : translations.labels?.createExamSchedule ||
                "Create Exam Schedule"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Row 1 */}
          <div className="row">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.academicYear || "Academic Year"}
                name="academicYear"
                type="text"
                value={academic_year}
                disabled
                readOnly
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.examName || "Exam Name"}
                name="examName"
                value={formData.examName}
                onChange={(value) => handleDropdownChange("examName", value)}
                options={examNameOptions}
                placeholder={
                  translations.labels?.selectExamName || "Select Exam"
                }
                required
                error={errors.examName}
                disabled={!!editId}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.class || "Class"}
                name="classId"
                value={formData?.classId?.label}
                onChange={(value) => handleDropdownChange("classId", value)}
                options={classOptions}
                placeholder={
                  translations.labels?.classPlaceholder || "Select Class"
                }
                required
                error={errors.classId}
                disabled={!!editId}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="row align-items-end">
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.section || "Section"}
                name="sectionId"
                value={formData.sectionId}
                onChange={(value) => handleDropdownChange("sectionId", value)}
                options={sectionOptions}
                placeholder={
                  translations.labels?.sectionPlaceholder || "Select Section"
                }
                required
                error={errors.sectionId}
                disabled={!!editId}
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.startDate || "Start Date"}
                name="startDate"
                type="text"
                value={
                  formData.startDate
                    ? dayjs(formData.startDate).format("DD-MM-YYYY")
                    : "-"
                }
                disabled
                readOnly
              />
            </div>

            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.endDate || "End Date"}
                name="endDate"
                type="text"
                value={
                  formData.endDate
                    ? dayjs(formData.endDate).format("DD-MM-YYYY")
                    : "-"
                }
                disabled
                readOnly
              />
            </div>
          </div>

          {/* Loading */}
          {loading && !editId && (
            <div className={styles.loadingContainer}>
              {translations.labels?.loading || "Loading..."}
            </div>
          )}

          {/* Subjects Table */}
          {subjects.length > 0 && (
            <>
              {/* Note shown only when exam status is Inactive (inputs locked) */}
              {inputsDisabled && (
                <div
                  style={{
                    backgroundColor: "#dbeafe",
                    border: "1px solid #bfdbfe",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    marginTop: "16px",
                    marginBottom: "4px",
                    // color: "#1e293b",
                    fontSize: "15px",
                    lineHeight: "1.5",
                  }}
                >
                  <span style={{ fontWeight: 700 }}>
                    {translations.labels?.note || "Note"}:
                  </span>{" "}
                  {translations.labels?.examScheduleInactiveNote ||
                    "You can add this exam schedule now, but the Date of Exam, Pass Mark, Maximum Mark, From Time and To Time can only be edited once the exam status is Active. To enter these details, make the exam Active and then edit this schedule."}
                </div>
              )}

              <div className="table-responsive mt-3">
                <table
                  className="table table-bordered mb-0"
                  style={{ tableLayout: "fixed", width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: "18%" }}>
                        {translations.labels?.subject || "Subject"}
                      </th>
                      <th style={{ width: "15%" }}>
                        {translations.labels?.examMode || "Exam Mode"}
                      </th>
                      <th style={{ width: "10%", textAlign: "center" }}>
                        {translations.labels?.includeSubject ||
                          "Include Subject"}
                      </th>
                      <th style={{ width: "15%" }}>
                        {translations.labels?.dateOfExam || "Date of Exam"}
                      </th>
                      <th style={{ width: "10%" }}>
                        {translations.labels?.passMark || "Pass Mark"}
                      </th>
                      <th style={{ width: "10%" }}>
                        {translations.labels?.maximumMark || "Maximum Mark"}
                      </th>
                      <th style={{ width: "10%" }}>
                        {translations.labels?.fromTime || "From Time"}
                      </th>
                      <th style={{ width: "10%" }}>
                        {translations.labels?.toTime || "To Time"}
                      </th>
                      <th style={{ width: "9%" }}>
                        {translations.labels?.durationHrs || "Duration (hrs)"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subjects.map((subject, index) => (
                      <tr
                        key={`${subject.subject_id}_${subject.mode_of_exam}_${index}`}
                      >
                        <td>
                          <CustomTextInput
                            name={`subject_${index}`}
                            type="text"
                            value={subject.subject_name}
                            disabled
                            readOnly
                          />
                        </td>

                        <td>
                          <CustomTextInput
                            name={`exam_mode_${index}`}
                            type="text"
                            value={subject.exam_mode}
                            disabled
                            readOnly
                          />
                        </td>

                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          <CustomCheckbox
                            name={`include_${index}`}
                            value={subject.include}
                            onChange={() => handleIncludeToggle(index)}
                            disabled={inputsDisabled}
                          />
                        </td>

                        <td>
                          <CustomDatePicker
                            name={`date_of_exam_${index}`}
                            value={
                              subject.date_of_exam
                                ? dayjs(subject.date_of_exam)
                                : null
                            }
                            onChange={(value) =>
                              handleSubjectChange(index, "date_of_exam", value)
                            }
                            placeholder={
                              translations.labels?.startDatePlaceholder ||
                              "Select date"
                            }
                            size="sm"
                            maxDate={dayjs(formData.endDate)}
                            minDate={dayjs(formData.startDate)}
                            disabled={inputsDisabled}
                          />
                        </td>

                        {/* Pass Mark with onBlur validation */}
                        <td>
                          <CustomTextInput
                            name={`pass_mark_${index}`}
                            value={subject.pass_mark}
                            onChange={(e) =>
                              handleSubjectChange(
                                index,
                                "pass_mark",
                                e.target.value,
                              )
                            }
                            onBlur={() => handleMarkBlur(index, "pass_mark")}
                            disabled={inputsDisabled}
                          />
                        </td>

                        {/* Maximum Mark with onBlur validation */}
                        <td>
                          <CustomTextInput
                            name={`max_mark_${index}`}
                            value={subject.max_mark}
                            onChange={(e) =>
                              handleSubjectChange(
                                index,
                                "max_mark",
                                e.target.value,
                              )
                            }
                            onBlur={() => handleMarkBlur(index, "max_mark")}
                            disabled={inputsDisabled}
                          />
                        </td>

                        <td>
                          <CustomTimePicker
                            name={`from_time_${index}`}
                            format="hh:mm a"
                            value={subject.from_time || null}
                            onChange={(value) =>
                              handleSubjectChange(index, "from_time", value)
                            }
                            placeholder="--:-- --"
                            disabled={inputsDisabled}
                          />
                        </td>

                        <td>
                          <CustomTimePicker
                            name={`to_time_${index}`}
                            format="hh:mm a"
                            value={subject.to_time || null}
                            onChange={(value) =>
                              handleSubjectChange(index, "to_time", value)
                            }
                            placeholder="--:-- --"
                            disabled={inputsDisabled}
                          />
                        </td>

                        <td>
                          <CustomTextInput
                            name={`duration_${index}`}
                            type="text"
                            value={subject.duration}
                            onChange={(e) =>
                              handleSubjectChange(
                                index,
                                "duration",
                                e.target.value,
                              )
                            }
                            disabled
                            readOnly
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.buttonContainer}>
                <CustomButton
                  type="submit"
                  variant="contained"
                  className={styles.submitButton}
                  fullWidth={false}
                  loading={submitting}
                  loadingText={
                    editId
                      ? translations.labels?.updating || "Updating..."
                      : translations.labels?.adding || "Adding..."
                  }
                >
                  {editId
                    ? translations.labels?.updateButton || "UPDATE"
                    : translations.labels?.add || "ADD"}
                </CustomButton>

                <CustomButton
                  type="button"
                  variant="contained"
                  className={styles.cancelButton}
                  fullWidth={false}
                  onClick={handleCancel}
                >
                  {translations.labels?.cancelButton || "CANCEL"}
                </CustomButton>
              </div>
            </>
          )}
        </form>
      </div>
    </>
  );
};

export default ExamScheduleCreate;
